import * as EmailValidator from 'email-validator';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  ResetPasswordModal,
  VerifyEmailModal,
} from '../../components/Molecules';
import { ForgotPasswordTemplate } from '../../components/Templates';
import { RecentLoginInfo } from '../../components/Templates/Auth/ForgotPasswordTemplate/ForgotPasswordTemplate';
import { useShareData } from '../../context';
import {
  Profile,
  ResetPasswordResult,
  UserListType,
} from '../../models/UserStore';
import { useUserStore } from '../../stores/UserStore';

const { findProfile, forgotPassword, sendVerification } =
  useUserStore.getState();

enum VerifyEmailType {
  haveSent = 'haveSent',
  notSend = 'notSend',
}

interface State {
  username: string;
  email: string;
  profile: Profile | undefined | null;
  isLoading: boolean;
  showPopup: VerifyEmailType | undefined;
  errorMessage: string | undefined;
  isEmailSent: boolean;
}

interface NavigationState {
  email?: string;
}

const ForgotPasswordScreen: React.FC = () => {
  const { state: navigationState } = useLocation() as {
    state: NavigationState | null;
  };
  const { t } = useTranslation();
  const [state, setState] = useState<State>({
    email: '',
    username: navigationState?.email ?? '',
    profile: undefined,
    isLoading: false,
    showPopup: undefined,
    errorMessage: undefined,
    isEmailSent: false,
  });

  const { setSharedData } = useShareData();

  useEffect(() => {
    setSharedData(undefined);
  }, [setSharedData]);

  const onUsernameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({
        ...s,
        username: event.target.value,
        errorMessage: undefined,
      }));
    },
    [],
  );

  function maskEmail(email: string): string {
    const parts: string[] = email.split('@');
    if (parts.length === 2) {
      const firstPart: string = parts[0]!;
      const secondPart: string = parts[1]!;
      if (firstPart.length > 3 && secondPart.length > 3) {
        const prefix: string = firstPart.substring(0, 3);
        const suffix: string = secondPart.substring(3);
        return `${prefix}***@***${suffix}`;
      } else if (firstPart.length > 3) {
        const prefix: string = firstPart.substring(0, 3);
        return `${prefix}***@${secondPart}`;
      } else if (secondPart.length > 3) {
        const suffix: string = secondPart.substring(3);
        return `${firstPart}@***${suffix}`;
      }
    }
    return email;
  }
  const onClickSearch = useCallback(async () => {
    if (state.username) {
      setState((s) => ({ ...s, isLoading: true }));
      const isEmail = EmailValidator.validate(state.username);
      const res = await findProfile({
        email: isEmail ? state.username : undefined,
        username: !isEmail ? state.username : undefined,
      });
      setState((s) => ({ ...s, isLoading: false }));
      if (res.ok) {
        if (res.data.profile) {
          const profile = new Profile(res.data.profile);
          setState((s) => ({
            ...s,
            profile: profile,
            email: res.data.email?.address ?? '',
          }));
        } else {
          setState((s) => ({
            ...s,
            errorMessage:
              'No accounts matched. Please make sure you have entered the correct email address or username.',
          }));
        }
      }
    } else {
      setState((s) => ({
        ...s,
        errorMessage: 'Enter your email / username',
      }));
      // toast.error(t('There is something wrong.'));
    }
  }, [state.username]);

  const onClickReturn = useCallback(() => {
    setState((s) => ({ ...s, profile: undefined, email: '' }));
  }, []);

  const onClickSendEmail = useCallback(async () => {
    if (state.profile?.userId) {
      setState((s) => ({ ...s, isLoading: true }));
      const res = await forgotPassword({
        userId: state.profile?.userId,
      });
      setState((s) => ({ ...s, isLoading: false }));
      if (res === ResetPasswordResult.Success) {
        setState((s) => ({ ...s, showPopup: VerifyEmailType.haveSent }));
      } else {
        setState((s) => ({ ...s, showPopup: VerifyEmailType.notSend }));
      }
    } else {
      toast.error(t('There is something wrong.'));
    }
  }, [state.profile?.userId, t]);

  const onCloseModal = useCallback(() => {
    setState((s) => ({ ...s, showPopup: undefined, isEmailSent: false }));
  }, []);

  const onClickSendVerification = useCallback(async () => {
    if (state.profile?.userId) {
      setState((s) => ({ ...s, isLoading: true }));
      const res = await sendVerification({
        userId: state.profile?.userId,
      });
      setState((s) => ({ ...s, isLoading: false }));
      if (res === ResetPasswordResult.Success) {
        setState((s) => ({ ...s, isEmailSent: true }));
      } else {
        toast.error(
          t('The sending interval is too short. Please try again later.'),
        );
        setState((s) => ({ ...s, isEmailSent: true }));
      }
    } else {
      toast.error(t('There is something wrong.'));
    }
  }, [state.profile?.userId, t]);

  const onClickRecentAcc = useCallback(
    (profile: Profile, email: string | undefined, username: string) => {
      if (email) {
        setState((s) => ({
          ...s,
          profile: profile,
          email: email,
          username: username,
          isEmailSent: false,
        }));
      } else {
        toast.error(t('This account has no email.'));
      }
    },
    [state, t],
  );

  const recentLogins = useMemo<RecentLoginInfo[]>(() => {
    const store = useUserStore.getState();
    const recentAccounts = store.accounts.list(UserListType.RecentLogin).get();
    const recentProfiles = store.profiles.list(UserListType.RecentLogin).get();

    return recentAccounts
      .map((account, index) => {
        const profile = recentProfiles[index];
        if (!profile || !account) return null;
        return {
          icon: profile.avatar || undefined,
          userId: profile.userId || undefined,
          source: profile.profilePicture || undefined,
          name: profile.name || undefined,
          email: account.email?.address
            ? maskEmail(account.email.address)
            : undefined,
          onClick: () =>
            onClickRecentAcc(
              profile,
              account.email?.address || undefined,
              profile.name,
            ),
        } as RecentLoginInfo;
      })
      .filter((item): item is RecentLoginInfo => item !== null);
  }, [onClickRecentAcc]);

  return (
    <>
      <ForgotPasswordTemplate
        username={{ value: state.username, onChange: onUsernameChange }}
        profile={state.profile}
        email={maskEmail(state.email)}
        onClickSearch={() => {
          onClickSearch().catch(console.error);
        }}
        onClickReturn={onClickReturn}
        onClickSendEmail={() => {
          onClickSendEmail().catch(console.error);
        }}
        isLoading={state.isLoading}
        errorMessage={state.errorMessage}
        recentLoginInfo={recentLogins}
      />

      {state.showPopup === VerifyEmailType.haveSent ? (
        <ResetPasswordModal
          profile={state.profile}
          email={maskEmail(state.email)}
          onClose={onCloseModal}
        />
      ) : null}
      {state.showPopup === VerifyEmailType.notSend ? (
        <VerifyEmailModal
          email={maskEmail(state.email)}
          onYes={() => {
            onClickSendVerification().catch(console.error);
          }}
          onClose={onCloseModal}
          isSent={state.isEmailSent}
          setIsSent={() => {
            setState((s) => ({ ...s, isEmailSent: false }));
          }}
        />
      ) : null}
    </>
  );
};

export default ForgotPasswordScreen;
